import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useUpdatePOLineItem, useVerifyPOLineItem, POLineItem } from "@/hooks/usePOLineItems";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";

interface POLineItemEditorProps {
  item: POLineItem;
  onClose: () => void;
  userEmail: string;
}

export function POLineItemEditor({ item, onClose, userEmail }: POLineItemEditorProps) {
  const [formData, setFormData] = useState({
    sku: item.sku || "",
    description: item.description || "",
    quantity: item.quantity,
    unit_price: item.unit_price,
    discount_percent: item.discount_percent,
    discount_amount: item.discount_amount,
    line_total: item.line_total,
    needs_review: item.needs_review,
    review_notes: item.review_notes || "",
  });

  const updateMutation = useUpdatePOLineItem();
  const verifyMutation = useVerifyPOLineItem();
  const { toast } = useToast();

  const handleChange = (field: string, value: string | number | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync({
        id: item.id,
        updates: {
          sku: formData.sku,
          description: formData.description,
          quantity: Number(formData.quantity),
          unit_price: Number(formData.unit_price),
          discount_percent: Number(formData.discount_percent),
          discount_amount: Number(formData.discount_amount),
          line_total: Number(formData.line_total),
          needs_review: formData.needs_review,
          review_notes: formData.review_notes,
        },
      });
      toast({ title: "Line item updated" });
      onClose();
    } catch (err) {
      toast({ title: "Failed to update line item", variant: "destructive" });
    }
  };

  const handleVerify = async () => {
    try {
      await verifyMutation.mutateAsync({ id: item.id, email: userEmail });
      toast({ title: "Line item verified" });
      onClose();
    } catch (err) {
      toast({ title: "Failed to verify line item", variant: "destructive" });
    }
  };

  const isPending = updateMutation.isPending || verifyMutation.isPending;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(value);
  };

  const hasHistoricalData = item.expected_gmv > 0 || item.expected_nmv > 0;

  return (
    <Dialog open onOpenChange={() => !isPending && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Line Item</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* SKU and Description */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="sku">SKU</Label>
              <Input
                id="sku"
                value={formData.sku}
                onChange={(e) => handleChange("sku", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
              />
            </div>
          </div>

          {/* Quantity and Price */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="quantity">Quantity</Label>
              <Input
                id="quantity"
                type="number"
                value={formData.quantity}
                onChange={(e) => handleChange("quantity", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="unit_price">Unit Price ($)</Label>
              <Input
                id="unit_price"
                type="number"
                step="0.01"
                value={formData.unit_price}
                onChange={(e) => handleChange("unit_price", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="line_total">Line Total ($)</Label>
              <Input
                id="line_total"
                type="number"
                step="0.01"
                value={formData.line_total}
                onChange={(e) => handleChange("line_total", e.target.value)}
              />
            </div>
          </div>

          {/* Discount */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="discount_percent">Discount (%)</Label>
              <Input
                id="discount_percent"
                type="number"
                step="0.01"
                value={formData.discount_percent}
                onChange={(e) => handleChange("discount_percent", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="discount_amount">Discount Amount ($)</Label>
              <Input
                id="discount_amount"
                type="number"
                step="0.01"
                value={formData.discount_amount}
                onChange={(e) => handleChange("discount_amount", e.target.value)}
              />
            </div>
          </div>

          {/* Historical values (read-only) */}
          <div className="p-4 bg-muted rounded-lg">
            <h4 className="text-sm font-medium mb-1">Economic Values</h4>
            <p className="text-xs text-muted-foreground mb-3">
              {hasHistoricalData
                ? "Based on trailing 12-month historical performance"
                : "No historical sales data available for this SKU"}
            </p>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">GEV:</span>
                <span className="ml-2 font-medium">
                  {hasHistoricalData ? formatCurrency(item.expected_gmv) : "N/A"}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">NEV:</span>
                <span className="ml-2 font-medium">
                  {hasHistoricalData ? formatCurrency(item.expected_nmv) : "N/A"}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">NERV ({(item.advance_rate * 100).toFixed(0)}%):</span>
                <span className="ml-2 font-medium">
                  {hasHistoricalData ? formatCurrency(item.net_expected_realizable_value) : "N/A"}
                </span>
              </div>
            </div>
          </div>

          {/* Review status */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="space-y-1">
              <Label htmlFor="needs_review">Needs Review</Label>
              <p className="text-sm text-muted-foreground">
                Flag this item for admin review
              </p>
            </div>
            <Switch
              id="needs_review"
              checked={formData.needs_review}
              onCheckedChange={(checked) => handleChange("needs_review", checked)}
            />
          </div>

          {/* Review notes */}
          <div className="space-y-2">
            <Label htmlFor="review_notes">Review Notes</Label>
            <Textarea
              id="review_notes"
              value={formData.review_notes}
              onChange={(e) => handleChange("review_notes", e.target.value)}
              placeholder="Add notes about this line item..."
              rows={3}
            />
          </div>

        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          {!item.verified_at && (
            <Button
              variant="secondary"
              onClick={handleVerify}
              disabled={isPending}
            >
              {verifyMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Verify
            </Button>
          )}
          <Button onClick={handleSave} disabled={isPending}>
            {updateMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
